'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { clsx } from 'clsx';
import {
    useFloating,
    autoUpdate,
    flip,
    shift,
    useDismiss,
    useRole,
    useClick,
    useInteractions,
    useId,
    FloatingFocusManager,
} from '@floating-ui/react';
import { useNavigationContext } from './useNavigation';

interface MenuGroupProps {
    children: React.ReactNode;
    referenceRef: HTMLElement | null;
}

export const MenuGroup: React.FC<MenuGroupProps> = (props) => {
    const { children, referenceRef } = props;

    const { state } = useNavigationContext();

    const [popperEL, setPopperEl] = useState<HTMLElement | null>(null);

    const placement = useMemo(() => {
        if (state.isHeaderNav) {
            return 'top-start';
        }

        return 'bottom-start';
    }, [state.isHeaderNav]);

    const { refs, floatingStyles, context } = useFloating({
        placement,
        elements: {
            floating: popperEL,
        },
        open: !!popperEL,
        transform: false,
        onOpenChange: (open) => {
            setPopperEl(open ? popperEL : null);
        },
        middleware: [
            flip({
                crossAxis: false,
                fallbackAxisSideDirection: 'start',
            }),
            shift(),
        ],
        whileElementsMounted: (referenceEl, floatingEl, update) => {
            const cleanup = autoUpdate(referenceEl, floatingEl, update);

            return cleanup;
        },
    });

    useEffect(() => {
        referenceRef && refs?.setReference(referenceRef);
    }, [refs, referenceRef]);

    const headingId = useId();

    const click = useClick(context);

    const dismiss = useDismiss(context);

    const role = useRole(context);

    const { getFloatingProps } = useInteractions([click, dismiss, role]);

    return (
        <FloatingFocusManager context={context} modal={false}>
            <div
                ref={refs.setFloating}
                style={floatingStyles}
                aria-labelledby={headingId}
                {...getFloatingProps()}
                hidden={!state.isMenuOpen}
                className={clsx('menu-group', {
                    'menu-group--header': state.isHeaderNav,
                    'menu-group--footer': state.isFooterNav,
                    'menu-group--body': state.isBodyNav,
                })}
            >
                <div className="menu-group-wrapper">{children}</div>
            </div>
        </FloatingFocusManager>
    );
};
